import { type ChangeEvent, useCallback, useState } from 'react';

import type { GDPRTranslations } from '../../types';
import type { NewsletterList } from '../Signup.types';

type Props = {
  newsletterLists: NewsletterList[];
  gdprTranslations?: GDPRTranslations;
};

export function NewsletterListPicker({ newsletterLists, gdprTranslations }: Props) {
  const [checked, setChecked] = useState<Record<string, boolean>>(() =>
    newsletterLists.reduce<Record<string, boolean>>((acc, list) => {
      acc[list.id] = list.auto_subscribe;
      return acc;
    }, {}),
  );

  const allChecked = newsletterLists.every((list) => checked[list.id]);
  const someChecked = newsletterLists.some((list) => checked[list.id]);

  const handleSelectAll = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const next = e.target.checked;
      const nextState: Record<string, boolean> = {};
      for (const list of newsletterLists) {
        nextState[list.id] = next;
      }
      setChecked(nextState);
    },
    [newsletterLists],
  );

  const handleToggle = useCallback((id: string, value: boolean) => {
    setChecked((prev) => ({ ...prev, [id]: value }));
  }, []);

  if (newsletterLists.length === 0) return null;

  // With a single list, the choice is implicit — submit the id via a hidden
  // input and skip the checkbox UI. Without this, the form would render a
  // pointless "select your newsletters" box with one already-checked option.
  if (newsletterLists.length === 1) {
    return (
      <>
        <input type="hidden" name="has_newsletter_lists" value="true" />
        <input type="hidden" name="newsletter_list_ids[]" value={newsletterLists[0].id} />
      </>
    );
  }

  const showSelectAll = newsletterLists.length >= 3;
  const chooseLabel = gdprTranslations?.chooseNewslettersText || 'Choose your newsletters';
  const selectAllLabel = gdprTranslations?.selectAllText || 'Select all';

  const checkboxStyle = {
    accentColor: 'var(--input-radio-checkbox-accent-color, var(--gdpr-checkbox-color))',
  };

  return (
    <div className="flex flex-col gap-3 w-full text-left" style={{ padding: '16px 0' }}>
      <input type="hidden" name="has_newsletter_lists" value="true" />
      <p
        className="font-medium"
        style={{
          color: 'var(--input-label-color, var(--input-text-color))',
          fontFamily: 'var(--input-font-family)',
          fontSize: 'var(--input-font-size, 14px)',
        }}
      >
        {chooseLabel}
      </p>

      {showSelectAll && (
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={allChecked}
            ref={(el) => {
              if (el) el.indeterminate = someChecked && !allChecked;
            }}
            onChange={handleSelectAll}
            className="h-4 w-4 rounded"
            style={checkboxStyle}
          />
          <span
            className="font-medium"
            style={{
              color: 'var(--input-label-color, var(--input-text-color))',
              fontFamily: 'var(--input-font-family)',
              fontSize: 'var(--input-font-size, 14px)',
            }}
          >
            {selectAllLabel}
          </span>
        </label>
      )}

      <div className="flex flex-col gap-2">
        {newsletterLists.map((list) => (
          <label key={list.id} className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              name="newsletter_list_ids[]"
              value={list.id}
              checked={!!checked[list.id]}
              onChange={(e) => handleToggle(list.id, e.target.checked)}
              className="h-4 w-4 shrink-0 rounded"
              style={checkboxStyle}
            />
            {list.logo_url && (
              <img src={list.logo_url} alt={list.name} className="h-10 w-10 shrink-0 rounded-full object-cover" />
            )}
            <div className="flex flex-col items-start gap-0.5 min-w-0">
              <span
                className="font-medium leading-snug"
                style={{
                  color: 'var(--input-label-color, var(--input-text-color))',
                  fontFamily: 'var(--input-font-family)',
                  fontSize: 'var(--input-font-size, 14px)',
                }}
              >
                {list.name}
              </span>
              {list.description && (
                <span
                  className="leading-snug opacity-75"
                  style={{
                    color: 'var(--input-label-color, var(--input-text-color))',
                    fontFamily: 'var(--input-font-family)',
                    fontSize: 'var(--input-font-size, 14px)',
                  }}
                >
                  {list.description}
                </span>
              )}
            </div>
          </label>
        ))}
      </div>
    </div>
  );
}
